import type { TFunction } from 'i18next';
import {
  ConversationTranscript,
  type DialogEventFilter,
  PERSON_ROLES,
  PHRASE_TYPES,
  type PhraseAnonymizationTag,
  type PhraseAttachmentInPhraseResponse,
} from '@/entities/conversations';
import { getShortName } from '@/shared/lib';

export type TranscriptPauseItem = {
  id: number;
  type: 'pause';
  text: string;
  data: ConversationTranscript;
  isSystem: true;
};

export type TranscriptMessageItem = {
  id: number;
  type: 'message';
  time: string;
  username: string;
  isIncoming: boolean;
  isLastInGroup: boolean;
  isFirstInGroup: boolean;
  text: string;
  role: string;
  data: ConversationTranscript;
  isSystem: false;
  wasInterrupted?: boolean;
  /**
   * Текст отметки о перебивании этой реплики. Заполняется, только когда перебивание прошло
   * отбор событий и выражается отметкой у самой реплики (параллельный показ);
   * в последовательном показе о перебивании говорит отдельное системное сообщение.
   */
  interruptionMark?: string;
  /** Роль перебившего — по ней видно, кем перебита реплика */
  interrupterRole?: string;
  /** Вложения, относящиеся к фразам этого облака (упорядочены по фразам и порядку в attachments). */
  attachments: PhraseAttachmentInPhraseResponse[];
  /** Подстановки анонимизации, встречающиеся в тексте фраз этого облака (без повторов). */
  anonymizationTags: PhraseAnonymizationTag[];
};

/**
 * Элемент «сессии перебивания»: реплики говорящего A, которого перебивали,
 * и реплики собеседника B, которые его перебили. Используется в режиме широкого
 * layout (>= 700px), когда обе колонки рисуются параллельно.
 */
export type TranscriptPairedItem = {
  id: number;
  type: 'paired';
  /** Реплики говорящего, которого перебивали (показываются на его «родной» стороне) */
  mainItems: TranscriptMessageItem[];
  /** Реплики перебивающего собеседника (показываются на противоположной стороне) */
  interruptingItems: TranscriptMessageItem[];
  /** Сторона расположения mainItems (определяется ролью говорящего) */
  mainSide: 'left' | 'right';
  isSystem: false;
};

export type TranscriptItem = TranscriptPauseItem | TranscriptMessageItem | TranscriptPairedItem;

type BuildTranscriptMessagesParams = {
  data: ConversationTranscript[];
  eventFilters: DialogEventFilter[];
  /** Форматирует метку времени события; принимает миллисекунды — в них приходят границы транскрипта */
  formatTime: (ms: number) => string;
  t: TFunction<['common', 'dialogs']>;
  /**
   * Если true — перебитые группы говорящего A и группы перебивающего B
   * объединяются в один TranscriptPairedItem, а системные сообщения
   * «Фраза была перебита» не вставляются: о перебивании говорит отметка
   * у самой перебитой реплики.
   */
  pairedLayout?: boolean;
};

type Block =
  | { kind: 'pause'; message: ConversationTranscript }
  | { kind: 'messageGroup'; messages: ConversationTranscript[] };

type BuildContext = {
  interruptedPhraseIds: Set<number>;
  interrupterRoleByPhraseId: Map<number, string>;
  formatTime: (ms: number) => string;
  t: TFunction<['common', 'dialogs']>;
};

const hasAttachments = (phrase: ConversationTranscript): boolean =>
  Array.isArray(phrase.attachments) && phrase.attachments.length > 0;

const groupIntoBlocks = (
  filteredData: ConversationTranscript[],
  eventFilters: DialogEventFilter[],
): Block[] => {
  const blocks: Block[] = [];
  let currentGroup: ConversationTranscript[] = [];

  const flushGroup = () => {
    if (currentGroup.length > 0) {
      blocks.push({ kind: 'messageGroup', messages: [...currentGroup] });
      currentGroup = [];
    }
  };

  for (const message of filteredData) {
    if (message.type === PHRASE_TYPES.PAUSE) {
      flushGroup();
      if (eventFilters.includes('pause')) {
        blocks.push({ kind: 'pause', message });
      }
      continue;
    }

    const userId = message.person!.id;
    const prevInGroup = currentGroup[currentGroup.length - 1];
    // Граница группировки: смена участника
    if (prevInGroup && prevInGroup.person!.id !== userId) {
      flushGroup();
    }
    // Граница группировки: предыдущая фраза в группе имеет вложения —
    // её нельзя склеивать со следующей даже того же участника
    if (prevInGroup && hasAttachments(prevInGroup)) {
      flushGroup();
    }
    // Текущая фраза с вложениями всегда отдельным сообщением:
    // сначала закрываем предыдущую группу, затем добавляем эту и сразу её закрываем
    if (hasAttachments(message)) {
      flushGroup();
      currentGroup.push(message);
      flushGroup();
      continue;
    }
    currentGroup.push(message);
  }
  flushGroup();

  return blocks;
};

/**
 * Проверяет, должно ли показываться сообщение о перебивании с учётом фильтра.
 * Логика фильтра:
 * - 'interruption' — все перебивания
 * - 'interruption_client' — только перебивания, где перебивающий = CLIENT
 * - 'interruption_employee' — только перебивания, где перебивающий = EMPLOYEE
 * - ни один из вариантов interruption не выбран — перебивания не показываются
 */
const shouldShowInterruption = (
  interrupterRole: string | undefined,
  eventFilters: DialogEventFilter[],
): boolean => {
  if (eventFilters.includes('interruption')) return true;
  if (interrupterRole === PERSON_ROLES.CLIENT && eventFilters.includes('interruption_client')) return true;
  if (interrupterRole === PERSON_ROLES.EMPLOYEE && eventFilters.includes('interruption_employee')) return true;
  return false;
};

const buildMessageItem = (
  group: ConversationTranscript[],
  ctx: BuildContext,
): { item: TranscriptMessageItem; interrupterRole: string | undefined } => {
  const first = group[0];
  const last = group[group.length - 1];
  const person = first.person!;
  const username = getShortName(person.firstName, person.lastName, person.middleName);
  const role = person.role === PERSON_ROLES.CLIENT ? ctx.t('userRoles.CLIENT') : ctx.t('userRoles.EMPLOYEE');
  const isIncoming = person.role === PERSON_ROLES.CLIENT;
  const startGroupTime = first.startedAt;
  const endGroupTime = last.finishedAt ?? last.startedAt;
  const text = group
    .map((m) => m.text ?? '')
    .join(' ')
    .trim();
  const wasInterrupted = group.some((m) => ctx.interruptedPhraseIds.has(m.id));
  // Роль перебивающего в этой группе (если фраза была перебита). Берём первый найденный,
  // т.к. подряд идущие фразы одного говорящего перебиваются одним и тем же оппонентом.
  const interrupterRole = group
    .map((m) => ctx.interrupterRoleByPhraseId.get(m.id))
    .find((r): r is string => r !== undefined);

  // Вложения — только из фраз этой группы
  const groupAttachments: PhraseAttachmentInPhraseResponse[] = [];
  for (const phrase of group) {
    if (Array.isArray(phrase.attachments)) groupAttachments.push(...phrase.attachments);
  }

  // Подстановки — из всех фраз группы; один и тот же тег может встретиться в нескольких фразах
  const groupTagsByPlaceholder = new Map<string, PhraseAnonymizationTag>();
  for (const phrase of group) {
    for (const tag of phrase.anonymizationTags ?? []) {
      if (!groupTagsByPlaceholder.has(tag.placeholder)) groupTagsByPlaceholder.set(tag.placeholder, tag);
    }
  }

  const item: TranscriptMessageItem = {
    id: first.id,
    type: 'message',
    time: `${ctx.formatTime(startGroupTime)} – ${ctx.formatTime(endGroupTime)}`,
    username,
    isIncoming,
    isLastInGroup: true,
    isFirstInGroup: true,
    text,
    role,
    data: { ...first, startedAt: startGroupTime, finishedAt: endGroupTime },
    isSystem: false,
    wasInterrupted,
    attachments: groupAttachments,
    anonymizationTags: [...groupTagsByPlaceholder.values()],
  };

  return { item, interrupterRole };
};

/**
 * Отметка внутри облака перебитой реплики (параллельный показ).
 * `byUnknown` — перебивший неизвестен, формулировка без указания стороны.
 */
const INTERRUPTION_MARK_KEYS = {
  byClient: 'dialogs:interruptedByClient',
  byEmployee: 'dialogs:interruptedByEmployee',
  byUnknown: 'dialogs:interruptedMark',
} as const;

/** Системное сообщение под перебитой репликой (последовательный показ) */
const INTERRUPTION_SYSTEM_MESSAGE_KEYS = {
  byClient: 'dialogs:previousPhraseInterruptedByClient',
  byEmployee: 'dialogs:previousPhraseInterruptedByEmployee',
  byUnknown: 'dialogs:previousPhraseInterrupted',
} as const;

/** Набор формулировок о перебивании: какую из набора взять — решает роль перебившего */
type InterruptionTextKeys = typeof INTERRUPTION_MARK_KEYS | typeof INTERRUPTION_SYSTEM_MESSAGE_KEYS;

/**
 * Текст о перебивании: по нему видно, кем перебита реплика. Формулировки у показов свои —
 * их задаёт набор ключей, — а выбор по роли перебившего у обоих один и тот же.
 */
const getInterruptionText = (
  interrupterRole: string | undefined,
  keys: InterruptionTextKeys,
  ctx: BuildContext,
): string => {
  if (interrupterRole === PERSON_ROLES.CLIENT) return ctx.t(keys.byClient);
  if (interrupterRole === PERSON_ROLES.EMPLOYEE) return ctx.t(keys.byEmployee);
  return ctx.t(keys.byUnknown);
};

/** Реплика с отметкой о перебивании — отметка ставится только прошедшему отбор перебиванию */
const withInterruptionMark = (
  item: TranscriptMessageItem,
  interrupterRole: string | undefined,
  ctx: BuildContext,
): TranscriptMessageItem => ({
  ...item,
  interruptionMark: getInterruptionText(interrupterRole, INTERRUPTION_MARK_KEYS, ctx),
  interrupterRole,
});

/** Перебивание этой реплики отмечается: оно есть и прошло отбор событий */
const isInterruptionShown = (
  item: TranscriptMessageItem,
  interrupterRole: string | undefined,
  eventFilters: DialogEventFilter[],
): boolean => Boolean(item.wasInterrupted) && shouldShowInterruption(interrupterRole, eventFilters);

const buildPauseItem = (message: ConversationTranscript, ctx: BuildContext): TranscriptPauseItem => {
  const finishedAt = message.finishedAt ?? message.startedAt;
  return {
    id: message.id,
    type: 'pause',
    text: ctx.t('dialogs:pauseFromTo', { from: ctx.formatTime(message.startedAt), to: ctx.formatTime(finishedAt) }),
    data: message,
    isSystem: true,
  };
};

export const buildTranscriptMessages = ({
  data,
  eventFilters,
  formatTime,
  t,
  pairedLayout = false,
}: BuildTranscriptMessagesParams): TranscriptItem[] => {
  // Карта: id фразы → роль её говорящего, нужна для определения роли перебивающего
  const phraseRoleById = new Map<number, string>();
  for (const message of data) {
    if (message.type === PHRASE_TYPES.PHRASE && message.person) {
      phraseRoleById.set(message.id, message.person.role);
    }
  }

  const interruptedPhraseIds = new Set<number>();
  // Карта: id прерванной фразы → роль перебивающего (из фразы interruptionPhraseId)
  const interrupterRoleByPhraseId = new Map<number, string>();
  data
    .filter((message) => message.type === PHRASE_TYPES.INTERRUPTION)
    .forEach((interruption) => {
      if (interruption.interruptedPhraseId) {
        interruptedPhraseIds.add(interruption.interruptedPhraseId);
        if (interruption.interruptionPhraseId) {
          const role = phraseRoleById.get(interruption.interruptionPhraseId);
          if (role) interrupterRoleByPhraseId.set(interruption.interruptedPhraseId, role);
        }
      }
    });

  const filteredData = data.filter(
    (message) =>
      message.type !== PHRASE_TYPES.INTERRUPTION && (message.person != null || message.type === PHRASE_TYPES.PAUSE),
  );

  const blocks = groupIntoBlocks(filteredData, eventFilters);
  const ctx: BuildContext = { interruptedPhraseIds, interrupterRoleByPhraseId, formatTime, t };

  return pairedLayout
    ? buildItemsPaired(blocks, ctx, eventFilters)
    : buildItemsSequential(blocks, ctx, eventFilters);
};

const buildItemsSequential = (
  blocks: Block[],
  ctx: BuildContext,
  eventFilters: DialogEventFilter[],
): TranscriptItem[] => {
  const result: TranscriptItem[] = [];

  for (const block of blocks) {
    if (block.kind === 'pause') {
      result.push(buildPauseItem(block.message, ctx));
      continue;
    }

    const { item, interrupterRole } = buildMessageItem(block.messages, ctx);
    const last = block.messages[block.messages.length - 1];
    result.push(item);

    if (isInterruptionShown(item, interrupterRole, eventFilters)) {
      result.push({
        id: last.id + 1000000,
        type: 'pause',
        text: getInterruptionText(interrupterRole, INTERRUPTION_SYSTEM_MESSAGE_KEYS, ctx),
        data: { ...last, type: PHRASE_TYPES.PAUSE, finishedAt: last.finishedAt ?? last.startedAt },
        isSystem: true,
      });
    }
  }

  return result;
};

/**
 * Сборка элементов для параллельного режима.
 *
 * Сессия перебивания — это последовательность смежных групп без пауз между ними,
 * где первая группа A была перебита. Сессия растёт пока:
 *   - следующая группа — это собеседник B (перебивающий) → добавляется в interruptingItems
 *   - следующая группа — это снова A → добавляется в mainItems, и если она тоже была
 *     перебита, сессия продолжается; если нет, это последняя реплика A в сессии,
 *     сессия закрывается на ней.
 *
 * Отбор событий действует здесь наравне с последовательным показом: перебивание, снятое
 * из отбора, не отмечается и сессию не открывает — реплики идут обычной чередой.
 * Системные сообщения «Фраза была перебита» в этом режиме не вставляются — о перебивании
 * говорит отметка у самой перебитой реплики, рядом с парой колонок. Отметку получает любая
 * перебитая реплика сессии — в том числе реплика собеседника B, которого перебили в ответ.
 */
const buildItemsPaired = (
  blocks: Block[],
  ctx: BuildContext,
  eventFilters: DialogEventFilter[],
): TranscriptItem[] => {
  const result: TranscriptItem[] = [];
  let i = 0;

  while (i < blocks.length) {
    const block = blocks[i];

    if (block.kind === 'pause') {
      result.push(buildPauseItem(block.message, ctx));
      i++;
      continue;
    }

    const { item: firstItem, interrupterRole } = buildMessageItem(block.messages, ctx);

    if (!isInterruptionShown(firstItem, interrupterRole, eventFilters)) {
      result.push(firstItem);
      i++;
      continue;
    }

    // Начало сессии перебивания
    const mainSpeakerId = block.messages[0].person!.id;
    const mainItems: TranscriptMessageItem[] = [withInterruptionMark(firstItem, interrupterRole, ctx)];
    const interruptingItems: TranscriptMessageItem[] = [];
    let sessionShouldContinue = true;
    let j = i + 1;

    while (j < blocks.length && sessionShouldContinue) {
      const next = blocks[j];
      // Пауза закрывает сессию
      if (next.kind === 'pause') break;

      const nextSpeakerId = next.messages[0].person!.id;
      const { item: nextItem, interrupterRole: nextInterrupterRole } = buildMessageItem(next.messages, ctx);

      const isNextInterruptionShown = isInterruptionShown(nextItem, nextInterrupterRole, eventFilters);
      const nextMarkedItem = isNextInterruptionShown
        ? withInterruptionMark(nextItem, nextInterrupterRole, ctx)
        : nextItem;

      if (nextSpeakerId === mainSpeakerId) {
        // Продолжение реплики A
        mainItems.push(nextMarkedItem);
        // Если эта реплика A не была перебита (или её перебивание снято из отбора) —
        // это финальная часть монолога, дальше сессию не растим
        if (!isNextInterruptionShown) {
          sessionShouldContinue = false;
        }
      } else {
        // Перебивание от B: его самого могли перебить в ответ — тогда отметка нужна и ему
        interruptingItems.push(nextMarkedItem);
      }
      j++;
    }

    // Если перебивающих реплик так и не оказалось (например, дальше сразу пауза) —
    // деградируем до обычного сообщения, без paired-обёртки
    if (interruptingItems.length === 0) {
      result.push(...mainItems);
    } else {
      result.push({
        id: firstItem.id,
        type: 'paired',
        mainItems,
        interruptingItems,
        mainSide: firstItem.isIncoming ? 'left' : 'right',
        isSystem: false,
      });
    }

    i = j;
  }

  return result;
};
